import React, { useId } from 'react';

import { ArrowIcon, FlipIcon, RotateLeftIcon, RotateRightIcon, ZoomInIcon, ZoomOutIcon } from './editorIcons';

import themeStyles from '../photoEditorTheme.module.css';

/** Zoom bounds; the zoom handlers clamp to the same range (utils/editorHandlers.ts). */
export const MIN_SCALE = 0.1;
export const MAX_SCALE = 3;

export interface PhotoEditorControlsProps {
  scale: number;
  flipX: boolean;
  /** False for a location map, which can't be zoomed. */
  showZoom: boolean;
  /** False for a location map, which can't be flipped. */
  showFlip: boolean;
  onMoveLeft: () => void;
  onMoveRight: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onRotateLeft: () => void;
  onRotateRight: () => void;
  onFlip: () => void;
}

/** "Zoom, Rotate & Flip", or the same list without the controls a mode hides. */
export function adjustCardTitle(showZoom: boolean, showFlip: boolean): string {
  const parts = [showZoom ? 'Zoom' : null, 'Rotate', showFlip ? 'Flip' : null].filter(
    (part): part is string => part !== null
  );
  if (parts.length === 1) return parts[0];
  return `${parts.slice(0, -1).join(', ')} & ${parts[parts.length - 1]}`;
}

interface RoundButtonProps {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  pressed?: boolean;
  children: React.ReactNode;
}

function RoundButton({ label, onClick, disabled = false, pressed, children }: RoundButtonProps) {
  return (
    <button
      type="button"
      className={themeStyles['photo-editor__round-button']}
      aria-label={label}
      aria-pressed={pressed}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
}

/**
 * The editor's two control cards, as in the Story of Us "Edit Photo" design: MOVE (left, right,
 * up, down) and ZOOM, ROTATE & FLIP (zoom in, zoom out, rotate left, rotate right, flip).
 */
export function PhotoEditorControls({
  scale,
  flipX,
  showZoom,
  showFlip,
  onMoveLeft,
  onMoveRight,
  onMoveUp,
  onMoveDown,
  onZoomIn,
  onZoomOut,
  onRotateLeft,
  onRotateRight,
  onFlip
}: PhotoEditorControlsProps) {
  const moveTitleId = useId();
  const adjustTitleId = useId();
  const titleClassName = themeStyles['photo-editor__card-title'] + ' body-sm body-uppercase font-medium';

  return (
    <>
      <section className={themeStyles['photo-editor__card']} aria-labelledby={moveTitleId}>
        <h3 id={moveTitleId} className={titleClassName}>
          Move
        </h3>
        <div className={themeStyles['photo-editor__card-buttons']}>
          <div className={themeStyles['photo-editor__button-pair']}>
            <RoundButton label="Move left" onClick={onMoveLeft}>
              <ArrowIcon className={themeStyles['photo-editor__icon--left']} />
            </RoundButton>
            <RoundButton label="Move right" onClick={onMoveRight}>
              <ArrowIcon />
            </RoundButton>
          </div>
          <div className={themeStyles['photo-editor__button-pair']}>
            <RoundButton label="Move up" onClick={onMoveUp}>
              <ArrowIcon className={themeStyles['photo-editor__icon--up']} />
            </RoundButton>
            <RoundButton label="Move down" onClick={onMoveDown}>
              <ArrowIcon className={themeStyles['photo-editor__icon--down']} />
            </RoundButton>
          </div>
        </div>
      </section>
      <section className={themeStyles['photo-editor__card']} aria-labelledby={adjustTitleId}>
        <h3 id={adjustTitleId} className={titleClassName}>
          {adjustCardTitle(showZoom, showFlip)}
        </h3>
        <div className={themeStyles['photo-editor__card-buttons']}>
          {showZoom && (
            <div className={themeStyles['photo-editor__button-pair']}>
              <RoundButton label="Zoom in" onClick={onZoomIn} disabled={scale >= MAX_SCALE}>
                <ZoomInIcon />
              </RoundButton>
              <RoundButton label="Zoom out" onClick={onZoomOut} disabled={scale <= MIN_SCALE}>
                <ZoomOutIcon />
              </RoundButton>
            </div>
          )}
          <div className={themeStyles['photo-editor__button-pair']}>
            <RoundButton label="Rotate left" onClick={onRotateLeft}>
              <RotateLeftIcon />
            </RoundButton>
            <RoundButton label="Rotate right" onClick={onRotateRight}>
              <RotateRightIcon />
            </RoundButton>
          </div>
          {showFlip && (
            <RoundButton label="Flip" onClick={onFlip} pressed={flipX}>
              <FlipIcon />
            </RoundButton>
          )}
        </div>
      </section>
    </>
  );
}
